import React from "react";
import { Link } from "react-router";
import PropTypes from "prop-types";
import InvisibleRecaptcha from "components/v3/recaptcha/InvisibleRecaptcha";
import RecaptchaNotice from "components/v3/recaptcha/RecaptchaNotice";
import EmailWithConfirm from "components/v3/email-with-confirm/EmailWithConfirm";
import Header from "components/v3/header/Header";
import PrimaryButton from "components/v3/primary-button/PrimaryButton";
import SocialSignIn from "utils/SocialSignIn";
import appPath from "utils/appPath";
import GoogleTracking from "services/GoogleTracking";
import SocialButtons from "components/v3/social-buttons/SocialButtons";
import TerminalSocialButton from "components/v3/terminal-social-button/TerminalSocialButton";
import AlertModal from "components/v3/alert-modal/AlertModal";
import ErrorHandler from "components/v3/error-handler/ErrorHandler";
import TermsAndPrivacyPolicyContainer from "containers/v3/TermsAndPrivacyPolicyContainer";
import LegalLinks from "components/v3/legal-links/LegalLinks";
import SubmitError from "components/v3/submit-error/SubmitError";
import { THEMES } from "common/constants/Constants";
import styles from "./styles.module.scss";

class Register extends React.Component {
    componentWillMount() {
        this._onSubmit = this._onSubmit.bind(this);
        this._socialLogin = this._socialLogin.bind(this);
        this._onEmailChange = this._onEmailChange.bind(this);
        this._onConfirmationEmailChange = this._onConfirmationEmailChange.bind(this);
    }

    componentWillUnmount() {
        this.socialSignIn.cleanup();
    }

    componentDidMount() {
        GoogleTracking.setDataLayerFields({
            action: "Register",
            cd9: "register",
            cd13: "regiser"
        });

        this.props.checkUser();
        this.socialSignIn = new SocialSignIn();
    }

    _onEmailChange(event) {
        event.preventDefault();

        this.props.onEmailChange(event.target.value.trim());
    }

    _onConfirmationEmailChange(event) {
        event.preventDefault();

        this.props.onConfirmationEmailChange(event.target.value.trim());
    }

    _socialLogin(socialSite) {
        GoogleTracking.registerClickEvent("reg-click", `connect_${ socialSite }`);

        this.socialSignIn.login(socialSite, () => {
            this.props.socialLogin(socialSite);
        });
    }

    _onSubmit(captchaResponse) {
        this.props.onSubmit(this.props.email, {
            useRecaptcha: this.props.useRecaptcha,
            captchaResponse
        });
    }

    render() {
        const {
            bloombergSiteUrl,
            signInLink,
            grecaptchaApiKey,
            useRecaptcha,
            email,
            confirmationEmail,
            errors,
            thinking,
            showAlertModal,
            hasBulkSubsRedirect = false
        } = this.props;

        const submitButtonDisabled = !email || !confirmationEmail || !!errors["email"] || !!errors["confirmEmail"];

        return (
            <div>
                <ErrorHandler type="register" errors={ errors }>
                    <SubmitError
                        theme={ THEMES.ERROR_THEME_MODULE_LEVEL }
                    />
                </ErrorHandler>
                <Header title="Create an account">
                    { hasBulkSubsRedirect && <BulkSubscriptionText /> }
                    <RegisterSubtitle signInLink={ signInLink } />
                </Header>
                <div className={ styles.body }>
                    <InvisibleRecaptcha
                        useRecaptcha={ useRecaptcha }
                        formStyles={ styles.registrationForm }
                        onCaptchaResponse={ this._onSubmit }
                        grecaptchaApiKey={ grecaptchaApiKey }
                        hiddenOnMobile={ appPath.isIframePath() }
                    >
                        <EmailWithConfirm
                            email={ email }
                            confirmEmail={ confirmationEmail }
                            errors={ errors }
                            handleEmailChange={ this._onEmailChange }
                            handleConfirmEmailChange={ this._onConfirmationEmailChange }
                        />
                        <TermsAndPrivacyPolicyContainer />
                        <div className={ styles.submitButton }>
                            <PrimaryButton
                                disabled={ submitButtonDisabled }
                                text="Continue"
                                thinking={ thinking }
                            />
                        </div>
                    </InvisibleRecaptcha>
                    <ErrorHandler type="socialLogin" errors={ errors }>
                        <AlertModal isOpen={ showAlertModal } onClose={ this.props.closeModal } />
                    </ErrorHandler>
                    { !hasBulkSubsRedirect && (
                        <div>
                            <div className={ styles.socialButtons }>
                                <SocialButtons onClick={ this._socialLogin } />
                            </div>
                            <TerminalSocialButton onClick={ this._socialLogin } />
                        </div>
                    ) }
                    <LegalLinks bloombergSiteUrl={ bloombergSiteUrl } tos={ false } privacy={ false } />
                    { appPath.isIframePath() && <RecaptchaNotice /> }
                </div>
            </div>
        );
    }
}

const RegisterSubtitle = ({ signInLink }) => {
    return (
        <span className={ styles.registerSubtitle }>
            Already have an account?
            <Link className={ styles.registerSubtitleLink } to={ signInLink }>Sign in</Link>
        </span>
    );
};

const BulkSubscriptionText = () => {
    return (
        <span className={ styles.bulkSubscriptionText }>
            Create an account to purchase a group subscription.
        </span>
    );
};

Register.propTypes = {
    signInLink: PropTypes.string.isRequired,
    bloombergSiteUrl: PropTypes.string,
    socialLogin: PropTypes.func.isRequired,
    onEmailChange: PropTypes.func.isRequired,
    onConfirmationEmailChange: PropTypes.func.isRequired,
    grecaptchaApiKey: PropTypes.string,
    useRecaptcha: PropTypes.bool,
    errors: PropTypes.object.isRequired,
    confirmationEmail: PropTypes.string,
    thinking: PropTypes.string,
    email: PropTypes.string.isRequired,
    checkUser: PropTypes.func.isRequired,
    closeModal: PropTypes.func.isRequired,
    hasBulkSubsRedirect: PropTypes.bool
};

export default Register;
